// ClaimInput.jsx - Fixed URL handling for encoded and Unicode URLs
import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ClaimInput = ({ onVerify, loading }) => {
  const [claim, setClaim] = useState('');
  const [inputType, setInputType] = useState('text');

  // Simple and reliable URL validation
  const isValidUrl = (value) => {
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  };

  const handleInputChange = (e) => {
    const value = e?.target?.value || '';
    setClaim(value);
    setInputType(isValidUrl(value.trim()) ? 'url' : 'text');
  };

  const handlePaste = (e) => {
    // Wait for paste to complete before checking
    setTimeout(() => {
      const value = e.target.value.trim();
      setInputType(isValidUrl(value) ? 'url' : 'text');
    }, 10);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (claim?.trim()) {
      onVerify({
        content: claim?.trim(),
        type: inputType
      });
    }
  };

  const handleClear = () => {
    setClaim('');
    setInputType('text');
  };

  const isValidInput = claim?.trim()?.length > 0;

  return (
    <div className="bg-card rounded-xl shadow-elevated border border-border p-6 lg:p-8">
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-primary rounded-lg flex items-center justify-center">
            <Icon name="CheckCircle" size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Fact Verification
            </h2>
            <p className="text-sm text-muted-foreground">
              Verify claims and check content credibility
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground">
              Enter claim or URL to verify
            </label>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <Icon
                name={inputType === 'url' ? 'Link' : 'Type'}
                size={14}
                color="var(--color-accent)"
              />
              <span className="capitalize">{inputType} detected</span>
            </div>
          </div>

          <div className="relative">
            <textarea
              value={claim}
              onChange={handleInputChange}
              onPaste={handlePaste}
              placeholder="Enter a claim to verify (e.g., 'The unemployment rate decreased by 2% last month') or paste a URL to analyze..."
              className="w-full h-32 px-4 py-3 border border-border rounded-lg bg-input text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-250"
              disabled={loading}
              spellCheck={false}
            />
            {claim && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleClear}
                className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
                disabled={loading}
              >
                <Icon name="X" size={16} />
              </Button>
            )}
          </div>

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{claim?.length} characters</span>
            <div className="flex items-center space-x-2">
              <span>Minimum 10 characters required</span>
              {claim && (
                <span className="text-accent font-medium">
                  Type: {inputType === 'url' ? 'URL 🔗' : 'Text 📝'}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="submit"
            variant="default"
            size="lg"
            loading={loading}
            disabled={!isValidInput || loading || claim?.trim()?.length < 10}
            className="flex-1 bg-gradient-primary hover:opacity-90 transition-opacity duration-250"
            iconName="Search"
            iconPosition="left"
          >
            {loading ? 'Verifying...' : 'Verify Claim'}
          </Button>

          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={handleClear}
            disabled={!claim || loading}
            iconName="RotateCcw"
            iconPosition="left"
          >
            Clear
          </Button>
        </div>
      </form>

      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} color="var(--color-accent)" className="mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium mb-1">How it works:</p>
            <ul className="space-y-1 text-xs">
              <li>• Enter any factual claim or paste a URL to analyze</li>
              <li>• Our AI cross-references multiple reliable sources</li>
              <li>• Get credibility scores and supporting evidence</li>
              <li>• Review detailed analysis and source links</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimInput;
